import { useCallback, useEffect } from 'react';
import { pushCapped } from './boardHelpers';
import { applyBoardPatch } from '../../extensions/bundled/task-manager/boardHistoryPatch';
import { noteBoardEdit } from '../../extensions/bundled/task-manager/boardChangeLog';

/**
 * Undo / redo for the open board: the bounded stacks live in TaskBoardTab
 * (a board switch resets them); this hook pushes, replays a step as an
 * inverse patch, and binds Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z and Ctrl+Y.
 * Moved out of TaskBoardTab unchanged.
 */
export default function useBoardHistory({
  boardCacheRef,
  boardId,
  boardModal,
  boardRef,
  deleteBoardTarget,
  deleteGroupTarget,
  dirtyRef,
  markDirtyEntity,
  recomputeSaveState,
  redoStackRef,
  scheduleDebounce,
  selectedTaskId,
  setBoard,
  syncHistoryFlags,
  undoStackRef,
}) {
  // Push a pre-edit snapshot onto the undo stack, capped at MAX_HISTORY (drop the
  // oldest), and clear redo — a fresh edit branches history. syncHistoryFlags is
  // left to the caller so a batch (e.g. drag commit) updates the buttons once.
  const pushUndo = useCallback((entry) => {
    if (!entry?.before || !entry?.after) return;
    pushCapped(undoStackRef.current, entry);
    redoStackRef.current = [];
  }, [redoStackRef, undoStackRef]);

  // Undo/redo as an INVERSE PATCH (boardHistoryPatch.js): only the entities
  // the history step changed are touched, so records that arrived since (a
  // teammate's card, a pulled change) are kept in the snapshot persist() writes.
  const applyHistoryStep = useCallback((from, to) => {
    if (!boardRef.current) return;
    const { board: next, touched } = applyBoardPatch(boardRef.current, from, to);
    if (!touched.length) return; // nothing left to revert: no write, no stale board stamp
    noteBoardEdit(boardRef.current, next);
    boardRef.current = next;
    boardCacheRef.current.set(next.id, next);
    dirtyRef.current.set(next.id, next);
    markDirtyEntity(next.id, touched);
    setBoard(next);
    scheduleDebounce();
    recomputeSaveState();
  }, [boardRef, boardCacheRef, dirtyRef, markDirtyEntity, setBoard, scheduleDebounce, recomputeSaveState]);

  const undo = useCallback(() => {
    const entry = undoStackRef.current.pop();
    if (!entry) return;
    pushCapped(redoStackRef.current, entry);
    applyHistoryStep(entry.after, entry.before);
    syncHistoryFlags();
  }, [applyHistoryStep, redoStackRef, syncHistoryFlags, undoStackRef]);

  const redo = useCallback(() => {
    const entry = redoStackRef.current.pop();
    if (!entry) return;
    pushCapped(undoStackRef.current, entry);
    applyHistoryStep(entry.before, entry.after);
    syncHistoryFlags();
  }, [applyHistoryStep, redoStackRef, syncHistoryFlags, undoStackRef]);

  // Ctrl/Cmd+Z = undo, Ctrl/Cmd+Shift+Z or Ctrl+Y = redo. Skipped when no board
  // is open, a modal is capturing input, or focus is in an editable field — so
  // in-place title editing keeps the browser's native text-undo.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!boardId || boardModal || selectedTaskId || deleteGroupTarget || deleteBoardTarget) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = e.key.toLowerCase();
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = (key === 'z' && e.shiftKey) || key === 'y';
      if (!isUndo && !isRedo) return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      e.preventDefault();
      if (isUndo) undo();
      else redo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [boardId, boardModal, selectedTaskId, deleteGroupTarget, deleteBoardTarget, undo, redo]);

  return { pushUndo, undo, redo };
}
